import React, { useState, useEffect, useMemo } from 'react';
import { Table, Dropdown, FormControl, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import SkeletonRow from '../../ui/LoadingSkeleton/SkeletonRow';
import Pagination from '../../ui/Pagination/Pagination';
import { getExpiredCpocReport, get90DayCpocReport, getNoCpocReport } from '../../../services/analytics/InsightsService';
import { useAuth } from '../../../firebase/AuthProvider';
import './Insights.css';

const reports = {
    "Expired CPOC Report": getExpiredCpocReport,
    "90-Day CPOC Expiration Report": get90DayCpocReport,
    "No CPOC Report": getNoCpocReport,
};

const Insights: React.FC = () => {
    const { currentUser } = useAuth();
    const token = (currentUser as any)?.accessToken;

    const [data, setData] = useState<any[]>([]);
    const [headers, setHeaders] = useState<string[]>([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: string } | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectedReport, setSelectedReport] = useState<string>(Object.keys(reports)[0]);
    const [filterTerm, setFilterTerm] = useState('');
    const [copyNotification, setCopyNotification] = useState({ visible: false, message: '' });

    useEffect(() => {
        const fetchReportData = async () => {
            if (!token) return;

            setIsLoading(true);
            setPage(1);

            const reportFetcher = reports[selectedReport as keyof typeof reports];
            if (reportFetcher) {
                const reportData = await reportFetcher(token);
                if (reportData) {
                    setData(reportData);
                    if (reportData.length > 0) {
                        setHeaders(Object.keys(reportData[0]));
                    } else {
                        setHeaders([]);
                    }
                } else {
                    setData([]);
                    setHeaders([]);
                }
            }
            setIsLoading(false);
        };

        fetchReportData();
    }, [selectedReport, token]);

    const filteredData = useMemo(() => {
        return data.filter(item =>
            Object.values(item).some(value =>
                String(value).toLowerCase().includes(filterTerm.toLowerCase())
            )
        );
    }, [data, filterTerm]);

    const sortedData = useMemo(() => {
        let sortableData = [...filteredData];
        if (sortConfig !== null) {
            sortableData.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableData;
    }, [filteredData, sortConfig]);

    const requestSort = (key: string) => {
        let direction = 'ascending';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const showCopyNotification = (message: string) => {
        setCopyNotification({ visible: true, message });
        setTimeout(() => {
            setCopyNotification({ visible: false, message: '' });
        }, 2000);
    };

    const handleCellCopy = (value: any) => {
        const textToCopy = value !== null && value !== undefined ? String(value) : '';
        navigator.clipboard.writeText(textToCopy);
        showCopyNotification(`'${textToCopy}' copied to clipboard!`);
    };

    const handleCopyAsCsv = () => {
        if (data.length === 0) return;
        const allHeaders = Object.keys(data[0] || {});
        const csvContent = [
            allHeaders.join(','),
            ...data.map(row =>
                allHeaders.map(header => JSON.stringify(row[header], (key, value) => value === null ? '' : value)).join(',')
            )
        ].join('\n');
        navigator.clipboard.writeText(csvContent);
        showCopyNotification('Entire table copied to clipboard as CSV!');
    };

    const handleCopyAsJson = () => {
        if (data.length === 0) return;
        const jsonContent = JSON.stringify(data, null, 2);
        navigator.clipboard.writeText(jsonContent);
        showCopyNotification('Entire table copied to clipboard as JSON!');
    };

    const handleCopyAsHtml = async () => {
        if (data.length === 0) return;
        try {
            const allHeaders = Object.keys(data[0] || {});
            const tableHtml = `<table><thead><tr>${allHeaders.map(header => `<th>${header}</th>`).join('')}</tr></thead><tbody>${data.map(row => `<tr>${allHeaders.map(header => `<td>${row[header] !== null && row[header] !== undefined ? row[header] : ''}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
            
            const blob = new Blob([tableHtml], { type: 'text/html' });
            const dataItem = new ClipboardItem({ 'text/html': blob });

            await navigator.clipboard.write([dataItem]);
            
            showCopyNotification('Entire table copied to clipboard as HTML!');
        } catch (err) {
            console.error('Failed to copy HTML table: ', err);
            showCopyNotification('Failed to copy table as HTML. Your browser may not support this feature.');
        }
    };

    const currentItems = sortedData.slice((page - 1) * pageSize, page * pageSize);
    const totalCount = filteredData.length;
    const totalPages = Math.ceil(totalCount / pageSize);

    const handlePageChange = (pageNumber: number) => setPage(pageNumber);

    const resetPageSize = (pageNumber: number, size: number) => {
        setPageSize(size);
        setPage(pageNumber);
    }

    return (
        <div className="container mt-4">
            {copyNotification.visible && (
                <div className="copy-notification">
                    {copyNotification.message}
                </div>
            )}
            <div className="top top_bar_flx border_tittle">
                <div className="top_bar_tittle d-flex align-items-center">
                    <h2>Insights Report</h2>
                    <Dropdown onSelect={(eventKey) => setSelectedReport(eventKey || '')} className="ms-3">
                        <Dropdown.Toggle variant="secondary" id="dropdown-basic" className="report-selector-dropdown">
                            {selectedReport}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {Object.keys(reports).map(reportName => (
                                <Dropdown.Item key={reportName} eventKey={reportName}>{reportName}</Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
                <div className="top_right_flx">
                    <InputGroup className="mb-3">
                        <FormControl
                            placeholder="Filter data..."
                            aria-label="Filter data"
                            value={filterTerm}
                            onChange={(e) => setFilterTerm(e.target.value)}
                            className="filter-input"
                        />
                    </InputGroup>
                </div>
            </div>
            <Table responsive className="common_table" style={{ tableLayout: 'fixed' }}>
                <thead>
                    <tr>
                        {headers.map((header) => (
                            <th key={header}>
                                <div className="table-header-content">
                                    <span>{header.toUpperCase()}</span>
                                    <FontAwesomeIcon className="cursor-pointer sort-icon" icon={faSort} onClick={() => requestSort(header)} />
                                </div>
                            </th>
                        ))}
                        <th style={{ width: '50px' }}>
                            <Dropdown>
                                <Dropdown.Toggle as="a" className="cursor-pointer ellipsis-dropdown-toggle">
                                    <FontAwesomeIcon icon={faEllipsisV} className="ellipsis-icon" />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={handleCopyAsCsv}>Copy as CSV</Dropdown.Item>
                                    <Dropdown.Item onClick={handleCopyAsJson}>Copy as JSON</Dropdown.Item>
                                    <Dropdown.Item onClick={handleCopyAsHtml}>Copy as HTML</Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        [...Array(pageSize)].map((_, index) => <SkeletonRow key={index} columns={headers.length + 1} />)
                    ) : currentItems.length > 0 ? (
                        currentItems.map((row, index) => (
                            <tr key={index}>
                                {headers.map((header) => {
                                    const value = row[header];
                                    const displayValue = value !== null && value !== undefined ? value : '--';
                                    return (
                                        <td key={header} title={String(displayValue)} onClick={() => handleCellCopy(displayValue)} className="copyable-cell">
                                            {String(displayValue)}
                                        </td>
                                    );
                                })}
                                <td></td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td className="text-center" colSpan={headers.length + 1}>
                                No data available
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
            {totalCount > 0 && !isLoading && (
                <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    pageSize={pageSize}
                    resetPageSize={resetPageSize}
                />
            )}
        </div>
    );
};

export default Insights;
